// ============================================================
// AutoLeadsAuditPage — Dashboard compacto de equidad AutoLeads
// v3 (2026-05-13): banda IA narrativa + sin scroll en subtarjetas
// ============================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { C } from '../ui/theme.js';
import { EmptyState } from '../ui/ui.jsx';
import { StatCard } from '../ui/premium.jsx';

const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

const headers = {
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

async function rest(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, { headers });
  if (!r.ok) throw new Error(`REST ${path} -> ${r.status}`);
  return r.json();
}

async function restWithCount(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: { ...headers, Prefer: 'count=exact' },
  });
  if (!r.ok) throw new Error(`REST ${path} -> ${r.status}`);
  const rows = await r.json();
  const cr = r.headers.get('content-range') || '';
  const count = parseInt(cr.split('/')[1], 10);
  return { rows, count: Number.isFinite(count) ? count : rows.length };
}

const FILTRO_AUTOMATIZACION = 'usuario_alta=in.(AutoAssign,AutoAssign-EdgeFn,AutoAssign-Manual,c360-backend)';

const inicioMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
};

// ── Formas del sistema de diseño de la suite ───────────────────────────────
// Tarjeta blanca de borde fino, tabla con cabecera en versalitas y píldoras de
// estado que sacan el fondo y el borde de un único color.
const card = {
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 12,
  padding: 14,
  fontFamily: 'inherit',
};
const th = { textAlign: 'left', padding: '9px 12px', background: '#f7f8fa', borderBottom: `1px solid ${C.line}`, fontWeight: 800, fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.3 };
const td = { padding: '9px 12px', borderBottom: '1px solid #eaecef', fontSize: 12.5, color: C.page };
const titulo = { margin: '0 0 8px', fontSize: 14, fontWeight: 800, color: C.ink, letterSpacing: 0.2 };

const pillStyle = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
  padding: '3px 10px', borderRadius: 999, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3,
  color, background: `${color}18`, border: `1px solid ${color}44`,
});

// Un color por nivel de alerta, tomado de la paleta de la suite.
const NIVEL_COLOR = { critico: C.rojo, alerta: C.naranja, aviso: C.brand, ok: C.verde };

const Pill = ({ nivel }) => (
  <span style={pillStyle(NIVEL_COLOR[nivel] || NIVEL_COLOR.ok)}>{nivel}</span>
);

const BalancePill = ({ pct, total }) => {
  if (total === 0) return <span style={pillStyle(C.muted)}>sin datos</span>;
  let color = C.verde, label = 'ok';
  if (pct >= 95) { color = C.rojo; label = 'roto'; }
  else if (pct >= 75) { color = C.naranja; label = 'desbal.'; }
  return <span style={pillStyle(color)}>{label} {Math.round(pct)}%</span>;
};

export default function AutoLeadsAuditPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    leadsMes: [], totalAutomatizacion: 0, totalBruto: 0,
    reglas: [], comerciales: [], stats: [], pendientes: [], origenes: [],
  });

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const inicio = inicioMes();
      const leadsQ = await restWithCount(
        `leads?select=lead_id,vendedor_nombre,marca,origen,tipo_venta,centro,origen_lead_id,created_at,usuario_alta&created_at=gte.${inicio}&${FILTRO_AUTOMATIZACION}&order=created_at.desc&limit=5000`
      );
      const totalBrutoQ = await restWithCount(`leads?select=lead_id&created_at=gte.${inicio}&limit=1`);
      const [reglas, comerciales, stats, pendientes, origenes] = await Promise.all([
        rest(`AutoLead_reglas?select=*&order=prioridad.asc,id.asc`),
        rest(`comerciales?select=id,nombre,activo,centro&activo=eq.true&order=id.asc`),
        rest(`AutoLead_comerciales_stats?select=*`),
        rest(`v_autolead_pendientes_alerta?select=*`),
        rest(`AutoLead_origenes?select=id,nombre,activo&order=orden.asc`),
      ]);
      setData({
        leadsMes: leadsQ.rows,
        totalAutomatizacion: leadsQ.count,
        totalBruto: totalBrutoQ.count,
        reglas, comerciales, stats, pendientes, origenes,
      });
    } catch (e) {
      console.error('AutoLeadsAuditPage error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const kpis = useMemo(() => {
    const totalAutomatizacion = data.totalAutomatizacion || data.leadsMes.length;
    const totalBruto = data.totalBruto || totalAutomatizacion;
    const viaInbox = data.leadsMes.filter(l => l.origen_lead_id != null).length;
    const cobertura = data.leadsMes.length ? Math.round(100 * viaInbox / data.leadsMes.length) : 0;
    const reglasActivas = data.reglas.filter(r => r.activo).length;
    const pendientesCriticos = data.pendientes.filter(p => p.nivel === 'critico').length;
    const pendientesAlertas = data.pendientes.filter(p => p.nivel === 'alerta').length;
    return { totalAutomatizacion, totalBruto, viaInbox, cobertura, reglasActivas, pendientesCriticos, pendientesAlertas };
  }, [data]);

  const repartoComercial = useMemo(() => {
    const map = new Map();
    for (const l of data.leadsMes) {
      const k = l.vendedor_nombre || '(sin asignar)';
      map.set(k, (map.get(k) || 0) + 1);
    }
    const arr = Array.from(map.entries()).map(([nombre, n]) => ({ nombre, n })).sort((a, b) => b.n - a.n);
    const total = arr.reduce((s, r) => s + r.n, 0) || 1;
    return arr.map(r => ({ ...r, pct: Math.round(1000 * r.n / total) / 10 }));
  }, [data.leadsMes]);

  const balancePool = useMemo(() => {
    const result = [];
    const cMap = new Map(data.comerciales.map(c => [String(c.id), c.nombre]));
    const seen = new Set();
    for (const r of data.reglas) {
      if (!r.activo) continue;
      const pool = Array.isArray(r.comerciales_pool) ? r.comerciales_pool : [];
      if (pool.length < 2) continue;
      const key = `${r.marca || '*'}|${r.origen || '*'}|${r.tipo_venta || '*'}|${r.centro || '*'}|${pool.join(',')}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const conteoMes = pool.map(id => {
        const nombre = cMap.get(String(id)) || `#${id}`;
        const n = data.leadsMes.filter(l =>
          l.vendedor_nombre === nombre
          && (r.marca ? l.marca === r.marca : true)
          && (r.origen ? l.origen === r.origen : true)
          && (r.tipo_venta ? (l.tipo_venta || '').toUpperCase() === r.tipo_venta.toUpperCase() : true)
        ).length;
        return { id, nombre, n };
      });
      const total = conteoMes.reduce((s, x) => s + x.n, 0);
      const max = Math.max(...conteoMes.map(x => x.n), 0);
      const pctMax = total ? (100 * max / total) : 0;
      result.push({ regla: r.nombre, pool: conteoMes, total, pctMax });
    }
    return result.sort((a, b) => b.pctMax - a.pctMax);
  }, [data.reglas, data.leadsMes, data.comerciales]);

  const reglasInactivas = useMemo(() => {
    return data.reglas.filter(r => r.activo).map(r => {
      const n = data.leadsMes.filter(l =>
        (r.marca ? l.marca === r.marca : true)
        && (r.origen ? l.origen === r.origen : true)
        && (r.tipo_venta ? (l.tipo_venta || '').toUpperCase() === r.tipo_venta.toUpperCase() : true)
      ).length;
      return { ...r, leads: n };
    }).filter(r => r.leads === 0);
  }, [data.reglas, data.leadsMes]);

  const resumenIA = useMemo(() => {
    const fechaHoy = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    const motorOk = kpis.totalAutomatizacion > 0 && kpis.cobertura >= 80;
    const urgentes = [];
    if (kpis.pendientesCriticos > 0) urgentes.push(`${kpis.pendientesCriticos} lead${kpis.pendientesCriticos > 1 ? 's' : ''} pendiente${kpis.pendientesCriticos > 1 ? 's' : ''} más de 48h`);
    if (kpis.pendientesAlertas > 0) urgentes.push(`${kpis.pendientesAlertas} lead${kpis.pendientesAlertas > 1 ? 's' : ''} pendiente${kpis.pendientesAlertas > 1 ? 's' : ''} entre 24 y 48h`);

    const observaciones = [];
    const poolsRotos = balancePool.filter(p => p.pctMax >= 95 && p.total > 0).length;
    const poolsDesbal = balancePool.filter(p => p.pctMax >= 75 && p.pctMax < 95 && p.total > 0).length;
    const poolsOk = balancePool.filter(p => p.total > 0 && p.pctMax < 75).length;
    if (poolsRotos > 0) observaciones.push(`${poolsRotos} pool${poolsRotos > 1 ? 's' : ''} con reparto histórico 100/0 (en transición desde el fix de stats)`);
    if (poolsDesbal > 0) observaciones.push(`${poolsDesbal} pool${poolsDesbal > 1 ? 's' : ''} con reparto histórico desequilibrado (se nivelará con los nuevos leads)`);
    const reglasB2B = reglasInactivas.filter(r => (r.tipo_venta || '').toUpperCase() === 'B2B').length;
    const reglasOtras = reglasInactivas.length - reglasB2B;
    if (reglasB2B > 0) observaciones.push(`${reglasB2B} regla${reglasB2B > 1 ? 's' : ''} B2B sin tráfico este mes (normal si no hay leads B2B)`);
    if (reglasOtras > 0) observaciones.push(`${reglasOtras} regla${reglasOtras > 1 ? 's' : ''} no-B2B sin tráfico este mes`);
    if (kpis.cobertura < 95 && kpis.cobertura >= 80) {
      observaciones.push(`cobertura inbox ${kpis.cobertura}% (algunos leads sin FK al inbox, pendiente de backfill marginal)`);
    }

    const partes = [];
    partes.push(`Hoy ${fechaHoy}, AutoLeads ha procesado ${kpis.totalAutomatizacion} leads por automatización este mes (de un total de ${kpis.totalBruto} entradas incluyendo migración y traspasos manuales)`);
    partes.push(motorOk
      ? `el motor de asignación está funcionando correctamente y la trazabilidad de cada lead queda registrada en historial`
      : `el motor presenta una cobertura por debajo de lo esperado (${kpis.cobertura}%) y conviene revisarlo`
    );
    if (urgentes.length === 0) {
      partes.push(`no hay incidencias urgentes que requieran tu intervención`);
    } else {
      partes.push(`hay ${urgentes.length === 1 ? 'una incidencia' : `${urgentes.length} incidencias`} urgente${urgentes.length > 1 ? 's' : ''}: ${urgentes.join(' y ')}`);
    }
    if (poolsOk > 0) partes.push(`${poolsOk} pool${poolsOk > 1 ? 's' : ''} multi-comercial reparten de forma equilibrada`);
    if (observaciones.length > 0) {
      partes.push(`a tener en cuenta: ${observaciones.join('; ')}`);
    }
    const parrafo = partes.join('. ') + '.';
    const parrafoCapitalizado = parrafo.charAt(0).toUpperCase() + parrafo.slice(1);

    // Del color del semáforo salen también el fondo y el borde de la banda.
    let semaforo;
    if (kpis.pendientesCriticos > 0) {
      semaforo = { color: C.rojo, titulo: 'Acción requerida' };
    } else if (urgentes.length > 0) {
      semaforo = { color: C.naranja, titulo: 'Revisar pendientes' };
    } else {
      semaforo = { color: C.verde, titulo: motorOk ? 'Sistema operativo · todo en orden' : 'Sistema operativo' };
    }

    return { fecha: fechaHoy, semaforo, motorOk, urgentes, observaciones, parrafo: parrafoCapitalizado, poolsOk, poolsRotos, poolsDesbal };
  }, [kpis, balancePool, reglasInactivas]);

  // Mientras cargan los datos se dibuja el esqueleto de la pantalla con el
  // brillo del sistema, no una línea de texto sobre el vacío.
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="fg-shimmer" style={{ height: 118 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          {[0, 1, 2, 3, 4].map(i => <div key={i} className="fg-shimmer" style={{ height: 74 }} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr)', gap: 10 }}>
          <div className="fg-shimmer" style={{ height: 210 }} />
          <div className="fg-shimmer" style={{ height: 210 }} />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ ...card, background: `${C.rojo}14`, borderColor: `${C.rojo}44`, color: C.rojo, fontSize: 12.5, fontWeight: 700 }}>Error: {error}</div>
    );
  }

  return (
    <div className="fg-fade" style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12.5 }}>
      <div style={{
        background: `${resumenIA.semaforo.color}14`,
        border: `1px solid ${resumenIA.semaforo.color}44`,
        borderRadius: 12, padding: '14px 16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 320 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: C.ink, letterSpacing: -0.2 }}>🎯 AutoLeads · {resumenIA.fecha}</h2>
              <span style={pillStyle(resumenIA.semaforo.color)}>{resumenIA.semaforo.titulo}</span>
              {resumenIA.motorOk && (
                <span style={pillStyle(C.verde)}>● motor activo</span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: C.page }}>
              {resumenIA.parrafo}
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: C.muted, marginTop: 8 }}>
              {resumenIA.urgentes.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.rojo, display: 'inline-block' }} />
                  <strong>Urgente:</strong> {resumenIA.urgentes.join(', ')}
                </div>
              )}
              {resumenIA.urgentes.length === 0 && resumenIA.motorOk && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.verde, display: 'inline-block' }} />
                  <strong>Correcto:</strong> motor, trazabilidad y reparto funcionando
                </div>
              )}
              {resumenIA.observaciones.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.faint, display: 'inline-block' }} />
                  <strong>Ya corregido:</strong> dato del mes en transición
                </div>
              )}
            </div>
          </div>
          <button onClick={cargar} style={{
            background: C.brand, color: 'white', border: 'none',
            padding: '9px 16px', borderRadius: 10, cursor: 'pointer',
            fontWeight: 800, fontSize: 13, boxShadow: '0 3px 10px rgba(0,81,135,.28)',
          }}>🔄 Recargar</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <StatCard label="Automatización" value={kpis.totalAutomatizacion} sub={`de ${kpis.totalBruto} mes`} color={C.brand} />
        <StatCard label="Cobertura inbox" value={kpis.cobertura} suffix="%" sub={`${kpis.viaInbox}/${data.leadsMes.length}`} color={kpis.cobertura >= 95 ? C.verde : C.naranja} />
        <StatCard label="Reglas activas" value={kpis.reglasActivas} sub={`${reglasInactivas.length} sin tráfico`} color={C.brand} />
        <StatCard label="Pend. >48h" value={kpis.pendientesCriticos} sub="críticos" color={kpis.pendientesCriticos > 0 ? C.rojo : C.verde} />
        <StatCard label="Pend. 24-48h" value={kpis.pendientesAlertas} sub="alertas" color={kpis.pendientesAlertas > 0 ? C.naranja : C.verde} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr)', gap: 10 }}>
        <div className="fg-card" style={card}>
          <h3 style={titulo}>Reparto por comercial (mes)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Comercial</th>
                <th style={{ ...th, textAlign: 'right', width: 50 }}>Leads</th>
                <th style={{ ...th, width: 110 }}>%</th>
              </tr>
            </thead>
            <tbody>
              {repartoComercial.map(r => (
                <tr key={r.nombre}>
                  <td style={td}>{r.nombre}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: C.ink }}>{r.n}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, background: '#eef1f4', height: 10, borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ background: C.brand, height: '100%', borderRadius: 999, width: `${Math.min(r.pct * 4, 100)}%` }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, minWidth: 32, textAlign: 'right' }}>{r.pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="fg-card" style={card}>
          <h3 style={titulo}>Balance por pool multi-comercial</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Regla</th>
                <th style={th}>Reparto</th>
                <th style={{ ...th, width: 90 }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {balancePool.map((p, i) => (
                <tr key={i}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.regla}</td>
                  <td style={td}>
                    {p.pool.map((c, j) => (
                      <span key={j} style={{ marginRight: 8 }}>
                        <span style={{ fontWeight: 800, color: C.ink }}>{c.nombre.split(' ')[0]}</span>: {c.n}
                      </span>
                    ))}
                    <span style={{ color: C.faint, fontSize: 11 }}>({p.total})</span>
                  </td>
                  <td style={td}><BalancePill pct={p.pctMax} total={p.total} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 10 }}>
        <div className="fg-card" style={card}>
          <h3 style={titulo}>
            Leads pendientes <span style={{ color: C.faint, fontWeight: 700 }}>({data.pendientes.length})</span>
          </h3>
          {data.pendientes.length === 0 ? (
            <EmptyState icon="✅" title="Ningún lead pendiente de asignar." />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Origen</th>
                  <th style={th}>Marca</th>
                  <th style={th}>Cliente</th>
                  <th style={{ ...th, textAlign: 'right', width: 40 }}>h</th>
                  <th style={{ ...th, width: 70 }}>Nivel</th>
                </tr>
              </thead>
              <tbody>
                {data.pendientes.map(p => (
                  <tr key={p.inbox_id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.origen || '—'}</td>
                    <td style={td}>{p.marca || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>{[p.nombre, p.apellidos].filter(Boolean).join(' ') || '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{Math.round(p.horas_pendiente)}</td>
                    <td style={td}><Pill nivel={p.nivel} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="fg-card" style={card}>
          <h3 style={titulo}>
            Reglas activas sin tráfico <span style={{ color: C.faint, fontWeight: 700 }}>({reglasInactivas.length})</span>
          </h3>
          {reglasInactivas.length === 0 ? (
            <EmptyState icon="✅" title="Todas las reglas tienen tráfico este mes." />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Regla</th>
                  <th style={th}>Marca</th>
                  <th style={th}>Origen</th>
                  <th style={th}>TV</th>
                </tr>
              </thead>
              <tbody>
                {reglasInactivas.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.nombre}</td>
                    <td style={td}>{r.marca || '·'}</td>
                    <td style={td}>{r.origen || '·'}</td>
                    <td style={td}>{r.tipo_venta || '·'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, color: C.faint, textAlign: 'right' }}>
        Última actualización: {new Date().toLocaleTimeString('es-ES')} · Mes en curso · solo leads de automatización
      </div>
    </div>
  );
}
